import React, { useEffect, useCallback, useRef } from "react";
import { X, ChevronLeft, ChevronRight, Download, ArrowLeftRight } from "lucide-react";

/**
 * Full-screen image viewer with keyboard nav, mouse arrows, counter, ESC close.
 * iter 96g — Client feedback #1 + #2 (Karina Funaru, 27.07.2026):
 *   - Sursa image must open enlarged in-page (not just thumbnail)
 *   - Variations must NOT open in a new tab; use an in-page viewer with
 *     left/right navigation, counter "Varianta 2 din 5", ESC close.
 *
 * Props:
 *   images    = [{ src, alt, label }] — required
 *   index     = current image index (controlled)
 *   onIndexChange(nextIndex)
 *   onClose()
 *   onCompare?() optional — shows "Before/After" button
 *   labelPrefix?: string — override for "Varianta {i}/{n}" ("Sursă" for source-only)
 */
export default function LightboxGallery({
  images,
  index,
  onIndexChange,
  onClose,
  onCompare = null,
  labelPrefix = null,
}) {
  const total = images?.length || 0;
  const current = images?.[index];

  const goPrev = useCallback(() => {
    if (total <= 1) return;
    onIndexChange((index - 1 + total) % total);
  }, [index, total, onIndexChange]);

  const goNext = useCallback(() => {
    if (total <= 1) return;
    onIndexChange((index + 1) % total);
  }, [index, total, onIndexChange]);

  // Keyboard navigation: ← → ESC
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [goPrev, goNext, onClose]);

  // Prevent body scroll when lightbox open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const backdropRef = useRef(null);
  const onBackdropClick = (e) => {
    if (e.target === backdropRef.current) onClose();
  };

  if (!current) return null;
  const counter = labelPrefix
    ? labelPrefix
    : total > 1
      ? `${index + 1} / ${total}`
      : "";

  return (
    <div
      ref={backdropRef}
      onClick={onBackdropClick}
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center animate-in fade-in duration-150"
      data-testid="lightbox-gallery"
    >
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 z-10">
        <div className="text-xs sm:text-sm uppercase tracking-[0.3em] text-orange-400 font-medium select-none">
          {current.label || counter}
        </div>
        <div className="flex items-center gap-2">
          {onCompare && (
            <button
              type="button"
              onClick={onCompare}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white text-xs rounded transition"
              data-testid="lightbox-compare"
              title="Compară sursa cu randarea"
            >
              <ArrowLeftRight size={13} /> Before / After
            </button>
          )}
          {current.downloadUrl && (
            <a
              href={current.downloadUrl}
              download
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white text-xs rounded transition"
              data-testid="lightbox-download"
              onClick={(e) => e.stopPropagation()}
            >
              <Download size={13} /> Descarcă
            </a>
          )}
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center w-9 h-9 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white rounded transition"
            data-testid="lightbox-close"
            aria-label="Închide (ESC)"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Prev arrow */}
      {total > 1 && (
        <button
          type="button"
          onClick={goPrev}
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white rounded-full transition z-10"
          data-testid="lightbox-prev"
          aria-label="Anterior (←)"
        >
          <ChevronLeft size={22} />
        </button>
      )}

      {/* Image */}
      <img
        src={current.src}
        alt={current.alt || counter}
        className="max-h-[92vh] max-w-[92vw] object-contain select-none"
        draggable={false}
        data-testid="lightbox-image"
      />

      {/* Next arrow */}
      {total > 1 && (
        <button
          type="button"
          onClick={goNext}
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white rounded-full transition z-10"
          data-testid="lightbox-next"
          aria-label="Următorul (→)"
        >
          <ChevronRight size={22} />
        </button>
      )}

      {/* Bottom hint */}
      <div className="absolute bottom-3 sm:bottom-5 left-0 right-0 text-center text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-zinc-500 select-none pointer-events-none">
        {total > 1
          ? "Săgeți ← → · ESC pentru închidere"
          : "Click în afara imaginii sau ESC pentru închidere"}
      </div>
    </div>
  );
}
